import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormControl, Validators } from "@angular/forms";
import { AppService } from "../../app.service";

export interface McpConfigDialogData {
    userKey: string;
    apiKey: string;
    baseUrl: string;
    workspaceName?: string;
}

@Component({
    selector: "app-mcp-config-dialog",
    standalone: false,
    templateUrl: "./mcp-config-dialog.component.html",
    styleUrls: ["./mcp-config-dialog.component.css"],
})
export class McpConfigDialogComponent {
    privateKeyControl = new FormControl("", [Validators.required]);
    showPrivateKey = false;
    isGeneratingKey = false;
    generatedPrivateKey: string = "";
    keyGenerationSuccess = false;
    keyGenerationError: string = "";

    constructor(
        public dialogRef: MatDialogRef<McpConfigDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: McpConfigDialogData,
        private appService: AppService
    ) { }

    onCancel(): void {
        this.dialogRef.close();
    }

    togglePrivateKeyVisibility(): void {
        this.showPrivateKey = !this.showPrivateKey;
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                this.privateKeyControl.setValue(reader.result as string);
                this.keyGenerationSuccess = false;
                this.generatedPrivateKey = "";
            };
            reader.readAsText(file);
        }
    }

    async generateNewKeyPair(): Promise<void> {
        this.isGeneratingKey = true;
        this.keyGenerationError = "";
        this.keyGenerationSuccess = false;

        try {
            // Generate key pair
            const keyPair = await this.appService.getKeyPair();
            const publicKeyPEM = keyPair[0];
            const privateKeyPEM = keyPair[1];

            // Submit public key to server
            const submitKeyPayload = {
                apiKey: this.data.apiKey,
                publicKey: this.appService.convertPEMToBase64(publicKeyPEM),
                routeType: "UI_SESSION"
            };

            const submitKeyResp = await this.appService.postRequest(
                submitKeyPayload,
                "auth/submit-key"
            );

            if (submitKeyResp?.status !== "Success") {
                throw new Error(submitKeyResp?.message || "Key submission failed");
            }

            // Store the generated private key
            this.generatedPrivateKey = privateKeyPEM;
            this.privateKeyControl.setValue(privateKeyPEM);
            this.keyGenerationSuccess = true;

            this.appService.addSuccessMessage("New key pair generated successfully!");

        } catch (error: any) {
            console.error("Error generating key pair:", error);
            this.keyGenerationError = error.message || "Failed to generate key pair";
            this.appService.addErrorMessage(
                "Failed to generate key pair: " + (error.message || "Unknown error")
            );
        } finally {
            this.isGeneratingKey = false;
        }
    }

    downloadPrivateKey(): void {
        if (!this.generatedPrivateKey) return;

        const blob = new Blob([this.generatedPrivateKey], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.data.workspaceName || "workspace"}_privatekey.pem`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.appService.addSuccessMessage("Private key downloaded successfully!");
    }

    downloadMcpConfig(): void {
        const privateKey = this.privateKeyControl.value;

        if (!privateKey) {
            return;
        }

        // Format private key for JSON (escape newlines)
        const escapedPrivateKey = privateKey.trim().replace(/\n/g, "\\n");

        // Create MCP JSON configuration
        const mcpConfig = {
            args: ["<PATH>/xdb-mcp-server/dist/index.js"],
            command: "node",
            env: {
                XDB_API_KEY: this.data.apiKey,
                XDB_BASE_URL: this.data.baseUrl,
                XDB_PRIVATE_KEY: escapedPrivateKey,
                XDB_USER_KEY: this.data.userKey,
            },
            useBuiltInNode: true,
        };

        // Convert to JSON string with pretty formatting
        const jsonString = JSON.stringify(mcpConfig, null, 2);

        // Create blob and download
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `mcp-config-${this.data.userKey.substring(0, 8)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.dialogRef.close("downloaded");
    }
}